import { useState } from "react";
import { toast } from "sonner";
import { API, STAGES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, XCircle } from "lucide-react";
import { SmartDatePicker } from "./SmartDatePicker";

// L1 — the ONE status control, used on the client Opportunities tab, the Financials
// expanded row and the Weekly BD Playbook. A deal can move to any stage (forward or
// back) and be closed out as won or lost. Reason + date are required when closing out
// or moving backwards. Everything writes through PUT /opportunities/{id}; closing as
// lost carries the loss taxonomy to the existing brief capture. No new write route.
const WON_STAGE = "Closed \u2013 Won";
const LOST_STAGE = "Closed \u2013 Lost";
const NOT_PROCEEDING_STAGE = "Closed \u2013 Not Proceeding";
// Fixed loss taxonomy — mirrors services/waits_config.py loss_vocabulary.
const LOSS_WHAT = [
  "lost_to_competitor", "incumbent_retained", "done_in_house", "no_decision", "budget",
  "timing_deferred", "requirements_not_a_fit", "our_proposal_or_process",
  "sponsor_or_org_changed", "procurement_or_compliance_blocked",
];
const stageIndex = (s) => STAGES.findIndex((x) => x.id === s);
const SELECTABLE = STAGES.filter((s) => s.id !== LOST_STAGE);  // Lost has its own guided close

export function DealStatusControl({ dealId, currentStage, onChanged, canAct = true, testidPrefix = "status", compact = false }) {
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [showLoss, setShowLoss] = useState(false);
  const [loss, setLoss] = useState({ what_happened: "no_decision", account: "our_assessment", competitive: false, competitor: "", free_text: "" });
  const [busy, setBusy] = useState(false);

  const tid = (s) => `${testidPrefix}-${s}`;

  if (!canAct) {
    return <span className="text-xs text-slate-500" data-testid={tid("readonly")}>Status: <b>{currentStage || "—"}</b></span>;
  }

  const put = async (patch) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/opportunities/${dealId}`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "Could not update status"); return false; }
      onChanged && onChanged(d);
      return true;
    } catch { toast.error("Could not update status"); return false; }
    finally { setBusy(false); }
  };

  const movingBackward = target && stageIndex(target) >= 0 && stageIndex(currentStage) >= 0 && stageIndex(target) < stageIndex(currentStage);
  const closingOut = target === WON_STAGE || target === NOT_PROCEEDING_STAGE;
  const needReasonDate = movingBackward || closingOut;

  const applyStage = async () => {
    if (!target || target === currentStage) { toast.error("Pick a different stage"); return; }
    if (needReasonDate && !reason.trim()) { toast.error("A reason is required to move backwards or close a deal"); return; }
    if (needReasonDate && !date) { toast.error("A date is required to close a deal"); return; }
    const patch = { stage: target };
    if (needReasonDate) { patch.stage_change_reason = reason.trim(); patch.actual_close_date = date; }
    if (await put(patch)) { toast.success(`Moved to ${target}`); setTarget(""); setReason(""); }
  };

  const closeLost = async () => {
    if (!reason.trim()) { toast.error("A reason is required to close a deal as lost"); return; }
    if (!date) { toast.error("A date is required to close a deal as lost"); return; }
    const patch = { stage: LOST_STAGE, stage_change_reason: reason.trim(), actual_close_date: date,
                    loss: { ...loss, competitor: loss.competitive ? loss.competitor : "" } };
    if (await put(patch)) { toast.success("Closed as lost — recorded"); setShowLoss(false); setReason(""); }
  };

  return (
    <div className={compact ? "space-y-1.5" : "space-y-2"} data-testid={tid("control")}>
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        <span className="text-slate-500">Status:</span>
        <b className="text-slate-800" data-testid={tid("current")}>{currentStage || "—"}</b>
        <select value={target} onChange={(e) => setTarget(e.target.value)} disabled={busy}
          className="h-7 text-xs border border-slate-200 rounded px-1.5 bg-white" data-testid={tid("stage-select")}>
          <option value="">Move to…</option>
          {SELECTABLE.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <Button size="sm" className="h-7 text-xs" disabled={busy || !target || target === currentStage}
          data-testid={tid("apply")} onClick={applyStage}>Apply</Button>
        <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50"
          disabled={busy} data-testid={tid("close-lost")} onClick={() => setShowLoss((s) => !s)}>
          <XCircle className="w-3 h-3 mr-1" />Close as lost
        </Button>
      </div>

      {needReasonDate && !showLoss && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={tid("reason-row")}>
          <span className="text-[11px] text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{closingOut ? "Closing out" : "Moving backwards"} — reason and date required</span>
          <Input className="h-7 text-xs flex-1 min-w-[180px]" placeholder="Reason…" value={reason}
            data-testid={tid("reason")} onChange={(e) => setReason(e.target.value)} />
          <SmartDatePicker value={date} onChange={setDate} blockWeekends={false} testid={tid("date")} className="w-40" />
        </div>
      )}

      {showLoss && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-2 space-y-1.5" data-testid={tid("loss-panel")}>
          <div className="text-[11px] font-semibold text-rose-800">Close as lost — records the loss on the deal's brief (reused, one place)</div>
          <div className="flex flex-wrap gap-1.5">
            <select value={loss.what_happened} onChange={(e) => setLoss((l) => ({ ...l, what_happened: e.target.value }))}
              className="h-7 text-xs border border-rose-200 rounded px-1.5 bg-white" data-testid={tid("loss-what")}>
              {LOSS_WHAT.map((w) => <option key={w} value={w}>{w.replace(/_/g, " ")}</option>)}
            </select>
            <select value={loss.account} onChange={(e) => setLoss((l) => ({ ...l, account: e.target.value }))}
              className="h-7 text-xs border border-rose-200 rounded px-1.5 bg-white" data-testid={tid("loss-account")}>
              <option value="client_stated">client stated</option>
              <option value="our_assessment">our assessment</option>
            </select>
            <label className="flex items-center gap-1 text-xs text-slate-700">
              <input type="checkbox" checked={loss.competitive} data-testid={tid("loss-competitive")}
                onChange={(e) => setLoss((l) => ({ ...l, competitive: e.target.checked }))} /> competitive
            </label>
            {loss.competitive && (
              <Input className="h-7 text-xs w-40" placeholder="Competitor" value={loss.competitor}
                data-testid={tid("loss-competitor")} onChange={(e) => setLoss((l) => ({ ...l, competitor: e.target.value }))} />
            )}
          </div>
          <Input className="h-7 text-xs" placeholder="What happened (free text)…" value={loss.free_text}
            data-testid={tid("loss-free-text")} onChange={(e) => setLoss((l) => ({ ...l, free_text: e.target.value }))} />
          <div className="flex flex-wrap items-center gap-1.5">
            <Input className="h-7 text-xs flex-1 min-w-[160px]" placeholder="Reason for closing…" value={reason}
              data-testid={tid("loss-reason")} onChange={(e) => setReason(e.target.value)} />
            <SmartDatePicker value={date} onChange={setDate} blockWeekends={false} testid={tid("loss-date")} className="w-40" />
            <Button size="sm" className="h-7 text-xs bg-rose-600 hover:bg-rose-700" disabled={busy}
              data-testid={tid("loss-confirm")} onClick={closeLost}>Confirm lost</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DealStatusControl;
